import React, { useState } from 'react';
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Dialog, ElevatedButton, TextButton, TextInput } from '@producer/ui-kit';

import { Asset } from '../../../app/entities';

import styles from './update-link.modal.module.scss';

interface UpdateLinkModalProps {
  onCancel: () => void;
  onSubmit: (values: { address: string; name?: string }) => Promise<void>;
  asset?: Asset;
}

export const UpdateLinkModal: React.FC<UpdateLinkModalProps> = ({ onCancel, onSubmit, asset }) => {
  const [isLoading, setIsLoading] = useState(false);

  const isUpdate = !!asset;

  const schema = yup.object({
    address: yup.string().max(2048, 'Max 2048 characters').required('Address is required'),
    name: yup.string().max(64, 'Max 64 characters').optional(),
  });

  const { handleSubmit, control, formState } = useForm({
    defaultValues: {
      name: asset?.name || '',
      address: asset?.url || '',
    },
    mode: 'all',
    reValidateMode: 'onChange',
    resolver: yupResolver(schema),
  });

  const onHandleSubmit = async (values: { address: string; name?: string }) => {
    setIsLoading(true);

    await onSubmit(values);

    setIsLoading(false);
    onCancel();
  };

  return (
    <Dialog
      title={isUpdate ? 'Edit link' : 'Add link'}
      onClose={onCancel}
      actions={[
        <TextButton key="cancel" text="Cancel" onClick={onCancel} />,
        <ElevatedButton
          key="submit"
          isLoading={isLoading}
          disabled={isLoading || !formState.isValid}
          onClick={handleSubmit(onHandleSubmit)}
          text="Save"
        />,
      ]}
    >
      <form className={styles.form} onSubmit={handleSubmit(onHandleSubmit)}>
        <Controller
          control={control}
          name="address"
          render={({ field: { onChange, value, name, ref }, fieldState: { error } }) => (
            <TextInput
              ref={ref}
              value={value}
              name={name}
              onChange={onChange}
              label="Address"
              placeholder="https://..."
              errorMessage={error?.message}
              required
            />
          )}
        />

        <Controller
          control={control}
          name="name"
          render={({ field: { onChange, value, name, ref }, fieldState: { error } }) => (
            <TextInput
              ref={ref}
              value={value}
              name={name}
              onChange={onChange}
              label="Name"
              errorMessage={error?.message}
            />
          )}
        />
      </form>
    </Dialog>
  );
};
